import UAParser from "ua-parser-js";

export type SecurityDeviceKind = "mobile" | "tablet" | "desktop";

export interface ParsedUserAgent {
  /** Design format: "Chrome v150 — Windows". */
  label: string;
  kind: SecurityDeviceKind;
}

/** Requirement keys rendered in the cream panel under the password fields. */
export type PasswordRequirementKey =
  | "minLength"
  | "hasUpper"
  | "hasLower"
  | "hasNumberOrSymbol";

export type PasswordRequirements = Record<PasswordRequirementKey, boolean>;

/**
 * Requirement rows in the exact order the design lists them — the panel is a
 * two-column grid filled row by row.
 */
export const PASSWORD_REQUIREMENTS: {
  key: PasswordRequirementKey;
  label: string;
}[] = [
  { key: "minLength", label: "At least 8 characters" },
  { key: "hasUpper", label: "At least one uppercase letter" },
  { key: "hasLower", label: "At least one lowercase letter" },
  { key: "hasNumberOrSymbol", label: "At least one number or symbol" },
];

export const getPasswordRequirements = (
  password: string,
): PasswordRequirements => ({
  minLength: password.length >= 8,
  hasUpper: /[A-Z]/.test(password),
  hasLower: /[a-z]/.test(password),
  hasNumberOrSymbol: /[\d\W]/.test(password),
});

export const areAllPasswordRequirementsMet = (password: string): boolean =>
  Object.values(getPasswordRequirements(password)).every(Boolean);

const EM_DASH = "—";

/**
 * Turns a raw user-agent string into the compact "browser vMajor — OS" label
 * and the device kind that picks the row glyph.
 */
export const parseSessionUserAgent = (userAgent: string): ParsedUserAgent => {
  if (!userAgent) {
    return { label: "-", kind: "desktop" };
  }

  const { browser, os, device } = new UAParser(userAgent).getResult();
  const majorVersion = browser.version?.split(".")[0];
  const browserLabel = browser.name
    ? `${browser.name}${majorVersion ? ` v${majorVersion}` : ""}`
    : "Unknown browser";
  const label = os.name
    ? `${browserLabel} ${EM_DASH} ${os.name}`
    : browserLabel;

  const deviceType = device.type;
  const kind: SecurityDeviceKind =
    deviceType === "mobile" || deviceType === "wearable"
      ? "mobile"
      : deviceType === "tablet"
        ? "tablet"
        : "desktop";

  return { label, kind };
};
